import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import calculators from "@/components/calculators";

export default function CalculatorsSection() {
  return (
    <section
      id="calculators"
      data-testid="calculators-section"
      className="py-20 md:py-28 bg-[#F8FAFC]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-12">
          <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-gray-500">
            Financial Calculators
          </span>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold font-heading tracking-tight text-[#0F2D52]">
            Plan your finances with our easy-to-use loan calculators.
          </h2>
          <p className="mt-3 text-base text-gray-600 leading-relaxed">
            Free, instant and accurate — designed to help you make informed
            borrowing decisions before you talk to a lender.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {calculators.map((calculator, i) => {
            const Icon = calculator.icon;
            return (
              <motion.div
                key={calculator.id}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.45, delay: i * 0.05 }}
                whileHover={{ y: -6 }}
                data-testid={`calc-card-${i}`}
                className="group relative rounded-2xl bg-white border border-gray-200 p-6 shadow-[0_2px_8px_rgba(0,0,0,0.02)] hover:shadow-[0_16px_36px_rgba(15,45,82,0.08)] mm-transition overflow-hidden"
              >
                <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-[#D4AF37]/10 blur-2xl opacity-0 group-hover:opacity-100 mm-transition" />
                <div className="relative">
                  <div className="w-11 h-11 rounded-xl bg-[#0F2D52]/5 flex items-center justify-center mb-4 group-hover:bg-[#0F2D52]/10 mm-transition">
                    <Icon
                      className="h-5 w-5 text-[#1E5AA8]"
                      strokeWidth={1.6}
                    />
                  </div>
                  <h3 className="text-lg font-bold text-[#0F2D52] font-heading tracking-tight">
                    {calculator.title}
                  </h3>
                  <p className="mt-2 text-sm text-gray-600 leading-relaxed">
                    {calculator.description || calculator.subtitle}
                  </p>
                  <Link
                    to={`/calculators#${calculator.id}`}
                    data-testid={`calc-card-cta-${i}`}
                    className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-[#0F2D52] hover:text-[#1E5AA8] mm-transition"
                  >
                    {calculator.cta ||
                      `Open ${calculator.shortTitle ?? calculator.title}`}
                    <ArrowUpRight className="h-4 w-4" />
                  </Link>
                </div>
              </motion.div>
            );
          })}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{
              duration: 0.45,
              delay: calculators.length * 0.05,
            }}
            whileHover={{ y: -6 }}
            data-testid="calc-view-all"
            className="group"
          >
            <Link
              to="/calculators"
              className="relative flex h-full min-h-[278px] flex-col justify-between overflow-hidden rounded-2xl border border-[#0F2D52] bg-[#0F2D52] p-6 shadow-[0_2px_8px_rgba(0,0,0,0.02)] hover:shadow-[0_16px_36px_rgba(15,45,82,0.18)] mm-transition"
            >
              <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-white/5 blur-2xl opacity-0 group-hover:opacity-100 mm-transition" />

              <div className="relative">
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-white/10">
                  <ArrowUpRight
                    className="h-5 w-5 text-white"
                    strokeWidth={1.6}
                  />
                </div>

                <h3 className="font-heading text-lg font-bold tracking-tight text-white">
                  View All Calculators
                </h3>

                <p className="mt-2 text-sm leading-relaxed text-white/70">
                  Explore all our financial calculators to estimate EMIs, check
                  eligibility, compare loans, calculate savings, and make
                  smarter borrowing decisions.
                </p>
              </div>

              <div className="relative mt-5 inline-flex items-center gap-1 text-sm font-semibold text-white">
                View All Calculators
                <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </div>
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
